import type { Database } from './database.types';
import { effectiveEventEnd } from './event-time';

export type EventCheckInMode = Database['public']['Enums']['event_checkin_mode'];
export type EventRegistrationStatus = Database['public']['Enums']['event_registration_status'];

export const CHECK_IN_REGISTRATION_STATUS: EventRegistrationStatus = 'confirmed';

export type EventCheckInWindowEvent = Pick<
  Database['public']['Tables']['events']['Row'],
  'checkin_mode' | 'ends_at' | 'starts_at'
>;

type EventCheckInTiming = Readonly<{
  ends_at: string | null;
  starts_at: string;
}>;

export type EventCheckInEligibilityInput = Readonly<{
  actorCheckedIn: boolean;
  event: EventCheckInWindowEvent;
  isOperator: boolean;
  now: Date;
}>;

export type EventCheckInEligibility = Readonly<{
  actorCheckedIn: boolean;
  canScan: boolean;
  label: string;
  mode: EventCheckInMode;
  reason: 'disabled' | 'outside_window' | 'operator' | 'checked_in_chain' | 'not_allowed';
  windowClosesAt: string;
  windowIsOpen: boolean;
  windowOpensAt: string;
}>;

const CHECK_IN_WINDOW_BEFORE_START_HOURS = 2;
const CHECK_IN_WINDOW_AFTER_END_HOURS = 24;

/**
 * How long before the gun an operator's app should open straight onto the
 * scanner. Deliberately much tighter than the full manual check-in window:
 * operators can correct attendance for 24 hours after a run, but a finished
 * event should never hijack the launch screen.
 */
const CHECK_IN_AUTO_OPEN_MINUTES = 30;

export function canCheckInRegistrationStatus(status: EventRegistrationStatus): boolean {
  return status === CHECK_IN_REGISTRATION_STATUS;
}

export function checkInWindowFor(event: EventCheckInTiming) {
  const start = new Date(event.starts_at);
  const eventEnd = effectiveEventEnd(event);
  return {
    closesAt: new Date(eventEnd.getTime() + CHECK_IN_WINDOW_AFTER_END_HOURS * 60 * 60 * 1000),
    opensAt: new Date(start.getTime() - CHECK_IN_WINDOW_BEFORE_START_HOURS * 60 * 60 * 1000),
  };
}

export function isCheckInWindowOpen(event: EventCheckInTiming, now: Date): boolean {
  const window = checkInWindowFor(event);
  return now >= window.opensAt && now <= window.closesAt;
}

/**
 * The narrower window in which opening the app should land an operator directly
 * on the scanner: from half an hour before the start until the event is over.
 */
export function checkInAutoOpenWindowFor(event: EventCheckInTiming) {
  const start = new Date(event.starts_at);
  return {
    closesAt: effectiveEventEnd(event),
    opensAt: new Date(start.getTime() - CHECK_IN_AUTO_OPEN_MINUTES * 60 * 1000),
  };
}

export function isCheckInAutoOpenWindow(event: EventCheckInTiming, now: Date): boolean {
  const window = checkInAutoOpenWindowFor(event);
  return now >= window.opensAt && now <= window.closesAt;
}

/**
 * The line shown when the app has opened straight onto the camera, so an
 * operator who did not choose to be there knows which event they are scanning
 * for and that it is fine to start.
 */
export function checkInStationMessage(
  event: Readonly<{ starts_at: string; title: string }>,
  now: Date
): string {
  const minutes = Math.round((Date.parse(event.starts_at) - now.getTime()) / 60_000);

  let when: string;
  if (minutes >= 2) {
    when = `is starting in ${minutes} minutes`;
  } else if (minutes === 1) {
    when = 'is starting in 1 minute';
  } else if (minutes === 0) {
    when = 'is starting now';
  } else {
    // Past the gun. "Starting in -4 minutes" is the kind of thing that makes an
    // operator distrust the whole screen.
    when = 'is under way';
  }

  return `${event.title} ${when}, you can start checking people in`;
}

/**
 * The same idea from the runner's side of the scan: the app has opened onto
 * their code, and they need to know which run it is for and what to do with it.
 */
export function memberCheckInMessage(
  event: Readonly<{ starts_at: string; title: string }>,
  now: Date
): string {
  const minutes = Math.round((Date.parse(event.starts_at) - now.getTime()) / 60_000);

  let when: string;
  if (minutes >= 2) {
    when = `starts in ${minutes} minutes`;
  } else if (minutes === 1) {
    when = 'starts in 1 minute';
  } else if (minutes === 0) {
    when = 'is starting now';
  } else {
    when = 'is under way';
  }

  return `${event.title} ${when}. Show this code to get checked in`;
}

type AutoOpenCandidate = EventCheckInWindowEvent & Readonly<{ id: string }>;

/**
 * Which event, if any, an operator's app should jump into on launch. Events
 * with check-in switched off never qualify. When two runs overlap the one that
 * started earliest wins, because that is the one already underway.
 */
export function pickAutoOpenCheckInEvent<T extends AutoOpenCandidate>(
  events: readonly T[],
  now: Date
): T | null {
  const live = events
    .filter((event) => event.checkin_mode !== 'none' && isCheckInAutoOpenWindow(event, now))
    .sort((left, right) => Date.parse(left.starts_at) - Date.parse(right.starts_at));

  return live[0] ?? null;
}

export function eventCheckInEligibility({
  actorCheckedIn,
  event,
  isOperator,
  now,
}: EventCheckInEligibilityInput): EventCheckInEligibility {
  const window = checkInWindowFor(event);
  const windowIsOpen = now >= window.opensAt && now <= window.closesAt;
  const base = {
    actorCheckedIn,
    mode: event.checkin_mode,
    windowClosesAt: window.closesAt.toISOString(),
    windowIsOpen,
    windowOpensAt: window.opensAt.toISOString(),
  };

  if (event.checkin_mode === 'none') {
    return {
      ...base,
      canScan: false,
      label: 'Check-in off',
      reason: 'disabled',
    };
  }

  if (!windowIsOpen) {
    return {
      ...base,
      canScan: false,
      label: 'Check-in closed',
      reason: 'outside_window',
    };
  }

  if (isOperator) {
    return {
      ...base,
      canScan: true,
      label: 'Check in runners',
      reason: 'operator',
    };
  }

  if (event.checkin_mode === 'checked_in_chain' && actorCheckedIn) {
    return {
      ...base,
      canScan: true,
      label: 'Check in your friends',
      reason: 'checked_in_chain',
    };
  }

  return {
    ...base,
    canScan: false,
    label: 'Check-in unavailable',
    reason: 'not_allowed',
  };
}

export type EventPaymentStatus = Database['public']['Enums']['event_payment_status'];

/**
 * What the operator needs to know about money at the moment of the scan.
 *
 * `due` deliberately covers every state that is not a settled payment —
 * unpaid, still validating, awaiting human review, and outright failed. At the
 * start line the only question that matters is "can I wave this runner
 * through", and anything short of `succeeded` means a person should look at it.
 * `detail` carries the nuance so the operator can tell a failed card apart from
 * a receipt that nobody has reviewed yet.
 */
export type EventCheckInPaymentState = 'free' | 'paid' | 'due';

export type EventCheckInPayment = Readonly<{
  amountThb: number | null;
  detail: string | null;
  label: string;
  state: EventCheckInPaymentState;
}>;

export type EventCheckInPaymentInput = Readonly<{
  paymentStatus: EventPaymentStatus | null;
  priceThb: number;
}>;

function formatThb(amount: number): string {
  return `฿${amount.toLocaleString('en-US')}`;
}

/** Why a payment is not settled, phrased for an operator holding a phone. */
const UNSETTLED_PAYMENT_DETAIL: Partial<Record<EventPaymentStatus, string>> = {
  failed: 'Payment failed',
  manual_review: 'Receipt awaiting review',
  pending: 'No payment recorded',
  validating: 'Payment still validating',
};

export function eventCheckInPayment({
  paymentStatus,
  priceThb,
}: EventCheckInPaymentInput): EventCheckInPayment {
  // A free event has no money to settle, so payment rows are irrelevant even if
  // one exists from an earlier paid configuration of the same event.
  if (priceThb <= 0) {
    return { amountThb: null, detail: null, label: 'Free', state: 'free' };
  }

  if (paymentStatus === 'succeeded') {
    return {
      amountThb: priceThb,
      detail: null,
      label: `Paid ${formatThb(priceThb)}`,
      state: 'paid',
    };
  }

  const detail = UNSETTLED_PAYMENT_DETAIL[paymentStatus ?? 'pending'] ?? 'No payment recorded';

  return {
    amountThb: priceThb,
    detail,
    label: `Due ${formatThb(priceThb)}`,
    state: 'due',
  };
}
